/**
 * RuntimeManagerWeapon.ts
 *
 * Main weapon system manager. Handles input, weapon slots,
 * and coordinates with the projectile system.
 */

import Phaser from 'phaser';
import { WeaponSlot } from './WeaponSlot';
import {
  SlotType,
  WeaponState,
  SLOT_KEYCODES,
  Allegiance,
  DataManagerWeapon,
} from './DataManagerWeapon';
import { storage } from '../core/Storage';

// Weapon swap configuration (loaded from overrides)
interface WeaponSwapConfig {
  cost: number;
  cooldownMs: number;
}

const DEFAULT_SWAP_CONFIG: WeaponSwapConfig = {
  cost: 100,
  cooldownMs: 0,  // Set to 0 for instant swap during development
};

export interface RuntimeManagerWeaponConfig {
  scene: Phaser.Scene;
  /** Function to get player position */
  getPlayerPosition: () => { x: number; y: number };
  /** Function to get image point world position */
  getImagePoint?: (pointName: string) => { x: number; y: number } | undefined;
  /** Optional: custom key bindings */
  keyBindings?: Partial<Record<SlotType, number>>;
  /** Optional: weapon swap config from overrides */
  swapConfig?: WeaponSwapConfig;
}

export interface RuntimeManagerWeaponState {
  slots: Record<
    SlotType,
    {
      activeWeaponId: string | null;
      weapons: Array<{ id: string; state: WeaponState; ammo: number }>;
    }
  >;
}

/**
 * RuntimeManagerWeapon - Central weapon system controller
 */
export class RuntimeManagerWeapon {
  private scene: Phaser.Scene;
  private slots: Map<SlotType, WeaponSlot> = new Map();
  private getPlayerPosition: () => { x: number; y: number };
  private getImagePoint?: (pointName: string) => { x: number; y: number } | undefined;

  // Input
  private keys: Map<SlotType, Phaser.Input.Keyboard.Key> = new Map();
  private swapKeys: Map<SlotType, Phaser.Input.Keyboard.Key> = new Map();

  // State
  private enabled: boolean = true;

  // Weapon swap
  private swapConfig: WeaponSwapConfig;
  private lastSwapTime: number = 0;
  private biomatter: number = 0;

  // Cooldown for "weapon empty" notifications (prevent spam)
  private lastEmptyNotification: Map<SlotType, number> = new Map();
  private static readonly EMPTY_NOTIFICATION_COOLDOWN = 1000; // 1 second

  constructor(config: RuntimeManagerWeaponConfig) {
    this.scene = config.scene;
    this.getPlayerPosition = config.getPlayerPosition;
    this.getImagePoint = config.getImagePoint;
    this.swapConfig = config.swapConfig ?? DEFAULT_SWAP_CONFIG;

    // Create slots
    this.createSlots();

    // Setup input
    this.setupInput(config.keyBindings);

    // Setup swap input (1/2/3 keys)
    this.setupSwapInput();

    // Listen for events
    this.setupEventListeners();

    // Load initial biomatter
    this.loadBiomatter();
  }

  /**
   * Load biomatter from storage
   */
  private async loadBiomatter(): Promise<void> {
    this.biomatter = await storage.getBiomatter();
  }

  /**
   * Create weapon slots
   */
  private createSlots(): void {
    const slotTypes: SlotType[] = ['gun', 'tractor', 'bomb'];

    for (const slotType of slotTypes) {
      const slot = new WeaponSlot({
        slotType,
        scene: this.scene,
      });
      this.slots.set(slotType, slot);
    }
  }

  /**
   * Setup keyboard input
   */
  private setupInput(customBindings?: Partial<Record<SlotType, number>>): void {
    if (!this.scene.input.keyboard) return;

    const bindings = {
      gun: customBindings?.gun ?? SLOT_KEYCODES.gun,
      tractor: customBindings?.tractor ?? SLOT_KEYCODES.tractor,
      bomb: customBindings?.bomb ?? SLOT_KEYCODES.bomb,
    };

    for (const [slotType, keyCode] of Object.entries(bindings)) {
      const key = this.scene.input.keyboard.addKey(keyCode);
      this.keys.set(slotType as SlotType, key);
    }
  }

  /**
   * Setup swap input (1/2/3 keys to cycle weapons)
   */
  private setupSwapInput(): void {
    if (!this.scene.input.keyboard) {
      console.warn('[RuntimeManagerWeapon] No keyboard input available');
      return;
    }

    // 1 = cycle Tractor, 2 = cycle Gun, 3 = cycle Bomb
    const swapBindings: Record<SlotType, number> = {
      tractor: Phaser.Input.Keyboard.KeyCodes.ONE,
      gun: Phaser.Input.Keyboard.KeyCodes.TWO,
      bomb: Phaser.Input.Keyboard.KeyCodes.THREE,
    };

    // console.log('[RuntimeManagerWeapon] Setting up swap keys:', {
    //   ONE: Phaser.Input.Keyboard.KeyCodes.ONE,
    //   TWO: Phaser.Input.Keyboard.KeyCodes.TWO,
    //   THREE: Phaser.Input.Keyboard.KeyCodes.THREE,
    // });

    for (const [slotType, keyCode] of Object.entries(swapBindings)) {
      const key = this.scene.input.keyboard.addKey(keyCode);
      this.swapKeys.set(slotType as SlotType, key);
      // console.log(`[RuntimeManagerWeapon] Registered swap key for ${slotType}: keyCode=${keyCode}`);
    }

    // Debug: Listen for any keydown to see if Firefox is capturing keys
    // this.scene.input.keyboard.on('keydown', (event: KeyboardEvent) => {
    //   if (event.key === '1' || event.key === '2' || event.key === '3') {
    //     console.log(`[RuntimeManagerWeapon] Raw keydown: key="${event.key}", code="${event.code}", keyCode=${event.keyCode}`);
    //   }
    // });
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Listen for power-up collection
    this.scene.events.on('collectPowerup', this.onCollectPowerup, this);

    // Listen for player death
    this.scene.events.on('playerDied', this.onPlayerDied, this);

    // Cleanup
    this.scene.events.once('shutdown', this.destroy, this);
    this.scene.events.once('destroy', this.destroy, this);
  }

  /**
   * Handle power-up collection
   */
  private onCollectPowerup(data: {
    type: string;
    weaponId?: string;
    ammo?: number;
  }): void {
    if (!data.weaponId) return;

    const weaponDef = DataManagerWeapon.get(data.weaponId);
    if (!weaponDef) return;

    const slot = this.slots.get(weaponDef.slot);
    if (!slot) return;

    // Add weapon or ammo
    const added = slot.addWeapon(data.weaponId, data.ammo);

    if (added) {
      // Emit event for UI
      this.scene.events.emit('weaponUnlocked', {
        weaponId: data.weaponId,
        slot: weaponDef.slot,
        ammo: data.ammo,
      });

      // Auto-switch to new weapon if it's better
      const activeWeapon = slot.getActiveWeaponDefinition();
      if (!activeWeapon?.isBase && weaponDef.isBase === false) {
        slot.setActiveWeapon(data.weaponId);
        this.scene.events.emit('weaponSwitched', {
          slot: weaponDef.slot,
          newWeaponId: data.weaponId,
        });
      }
    }
  }

  /**
   * Handle player death
   */
  private onPlayerDied(): void {
    this.enabled = false;

    // Stop any held weapons
    const tractorSlot = this.slots.get('tractor');
    if (tractorSlot?.getIsHolding()) {
      tractorSlot.stopHold();
      this.scene.events.emit('tractorBeamStopped');
    }
  }

  /**
   * Update - call every frame
   */
  update(time: number, delta: number): void {
    if (!this.enabled) return;

    this.handleInput(time);
  }

  /**
   * Handle input for all slots
   */
  private handleInput(time: number): void {
    const playerPos = this.getPlayerPosition();

    // Weapon swap (1/2/3 keys)
    this.handleSwapInput(time);

    // Gun slot (X) - press to fire
    this.handleGunInput(time, playerPos);

    // Tractor slot (Z) - hold to activate
    this.handleTractorInput(time, playerPos);

    // Bomb slot (C) - press to fire
    this.handleBombInput(time, playerPos);
  }

  /**
   * Handle weapon swap input (1/2/3 keys)
   */
  private handleSwapInput(time: number): void {
    // Check each swap key
    for (const [slotType, key] of this.swapKeys) {
      // Debug: log key state
      // if (key.isDown) {
      //   console.log(`[RuntimeManagerWeapon] Key for ${slotType} is DOWN, keyCode=${key.keyCode}`);
      // }
      if (Phaser.Input.Keyboard.JustDown(key)) {
        // console.log(`[RuntimeManagerWeapon] JustDown detected for ${slotType}`);
        this.trySwapWeapon(slotType, time);
        break; // Only handle one swap per frame
      }
    }
  }

  /**
   * Try to swap weapon in a slot
   */
  private async trySwapWeapon(slotType: SlotType, time: number): Promise<void> {
    // console.log(`[RuntimeManagerWeapon] trySwapWeapon called for ${slotType}`);

    const slot = this.slots.get(slotType);
    if (!slot) {
      // console.log(`[RuntimeManagerWeapon] No slot found for ${slotType}`);
      return;
    }

    // Check if slot has multiple weapons to swap
    const activeWeapons = slot.getActiveWeapons();
    // console.log(`[RuntimeManagerWeapon] Slot ${slotType} has ${activeWeapons.length} active weapons:`, activeWeapons.map(w => w.definition.id));

    if (activeWeapons.length <= 1) {
      // console.log(`[RuntimeManagerWeapon] FAILED: Only ${activeWeapons.length} weapon(s) in slot`);
      this.scene.events.emit('swapFailed', {
        slot: slotType,
        reason: 'single_weapon',
        message: 'Only one weapon in slot',
      });
      return;
    }

    // Check cooldown
    const timeSinceLastSwap = time - this.lastSwapTime;
    if (timeSinceLastSwap < this.swapConfig.cooldownMs) {
      const remainingCooldown = Math.ceil((this.swapConfig.cooldownMs - timeSinceLastSwap) / 1000);
      // console.log(`[RuntimeManagerWeapon] FAILED: Cooldown - wait ${remainingCooldown}s`);
      this.scene.events.emit('swapFailed', {
        slot: slotType,
        reason: 'cooldown',
        message: `Wait ${remainingCooldown}s`,
      });
      return;
    }

    // Check biomatter cost
    // console.log(`[RuntimeManagerWeapon] Biomatter check: have=${this.biomatter}, cost=${this.swapConfig.cost}`);
    if (this.biomatter < this.swapConfig.cost) {
      // console.log(`[RuntimeManagerWeapon] FAILED: Not enough biomatter`);
      this.scene.events.emit('swapFailed', {
        slot: slotType,
        reason: 'no_biomatter',
        message: 'Not enough biomatter',
      });
      return;
    }

    // Deduct cost
    this.biomatter -= this.swapConfig.cost;
    await storage.saveBiomatter(this.biomatter);

    // Update cooldown
    this.lastSwapTime = time;

    // Perform swap
    const newWeaponId = slot.cycleWeapon();
    const newWeapon = slot.getActiveWeaponDefinition();

    // Emit events
    this.scene.events.emit('weaponSwapped', {
      slot: slotType,
      newWeaponId,
      newWeapon,
      cost: this.swapConfig.cost,
      remainingBiomatter: this.biomatter,
    });

    this.scene.events.emit('biomatterChanged', {
      amount: this.biomatter,
      delta: -this.swapConfig.cost,
    });
  }

  /**
   * Handle gun input
   */
  private handleGunInput(time: number, playerPos: { x: number; y: number }): void {
    const key = this.keys.get('gun');
    const slot = this.slots.get('gun');
    if (!key || !slot) return;

    if (Phaser.Input.Keyboard.JustDown(key)) {
      this.fireSlot('gun', time, playerPos);
    }
  }

  /**
   * Handle tractor input
   */
  private handleTractorInput(time: number, playerPos: { x: number; y: number }): void {
    const key = this.keys.get('tractor');
    const slot = this.slots.get('tractor');
    if (!key || !slot) return;

    if (key.isDown && !slot.getIsHolding()) {
      // Start holding
      slot.startHold(time);
      this.scene.events.emit('tractorBeamStarted', {
        weapon: slot.getActiveWeaponDefinition(),
        position: playerPos,
      });
    } else if (!key.isDown && slot.getIsHolding()) {
      // Stop holding
      slot.stopHold();
      this.scene.events.emit('tractorBeamStopped');
    }
  }

  /**
   * Handle bomb input
   */
  private handleBombInput(time: number, playerPos: { x: number; y: number }): void {
    const key = this.keys.get('bomb');
    const slot = this.slots.get('bomb');
    if (!key || !slot) return;

    if (Phaser.Input.Keyboard.JustDown(key)) {
      this.fireSlot('bomb', time, playerPos);
    }
  }

  /**
   * Fire a weapon slot
   */
  private fireSlot(
    slotType: SlotType,
    time: number,
    playerPos: { x: number; y: number }
  ): boolean {
    const slot = this.slots.get(slotType);
    if (!slot) return false;

    const fireResult = slot.fire(time, playerPos.x, playerPos.y);
    if (!fireResult || !fireResult.fired) {
      // Check if failure was due to empty ammo - emit weaponEmpty event (with cooldown)
      const ammo = slot.getAmmo();
      if (ammo === 0) {
        const weaponDef = slot.getActiveWeaponDefinition();
        // Only emit if weapon doesn't have infinite ammo AND cooldown has passed
        if (weaponDef && !weaponDef.hasInfiniteAmmo) {
          const lastNotification = this.lastEmptyNotification.get(slotType) ?? 0;
          if (time - lastNotification >= RuntimeManagerWeapon.EMPTY_NOTIFICATION_COOLDOWN) {
            this.lastEmptyNotification.set(slotType, time);
            this.scene.events.emit('weaponEmpty', {
              slot: slotType,
              weapon: weaponDef,
            });
          }
        }
      }
      return false;
    }

    // Resolve fire points using image points if available
    const weaponDef = slot.getActiveWeaponDefinition();
    let resolvedFirePoints = fireResult.firePoints;

    if (weaponDef?.imagePoints && this.getImagePoint) {
      const imagePointPositions: Array<{ x: number; y: number }> = [];
      for (const pointName of weaponDef.imagePoints) {
        const pos = this.getImagePoint(pointName);
        if (pos) {
          imagePointPositions.push(pos);
        }
      }
      // Use image points if we resolved at least one
      if (imagePointPositions.length > 0) {
        resolvedFirePoints = imagePointPositions;
      }
    }

    // Emit fired event
    this.scene.events.emit('weaponFired', {
      slot: slotType,
      weapon: weaponDef,
      firePoints: resolvedFirePoints,
    });

    // Spawn projectiles if this weapon has them
    if (fireResult.projectileType) {
      const burstCount = fireResult.burst?.count ?? 1;
      const burstDelay = fireResult.burst?.delay ?? 0;

      for (let i = 0; i < burstCount; i++) {
        const spawnFn = () => {
          const initialScale = fireResult.burst?.initialScales?.[i];

          // Recalculate fire points for each burst shot (ship may have moved)
          let currentFirePoints = resolvedFirePoints;
          if (weaponDef?.imagePoints && this.getImagePoint) {
            const freshPositions: Array<{ x: number; y: number }> = [];
            for (const pointName of weaponDef.imagePoints) {
              const pos = this.getImagePoint(pointName);
              if (pos) {
                freshPositions.push(pos);
              }
            }
            if (freshPositions.length > 0) {
              currentFirePoints = freshPositions;
            }
          }

          for (const firePoint of currentFirePoints) {
            this.scene.events.emit('spawnProjectile', {
              type: fireResult.projectileType,
              x: firePoint.x,
              y: firePoint.y,
              angle: fireResult.angle,
              speed: fireResult.speed,
              damage: fireResult.damage,
              allegiance: Allegiance.Player,
              initialScaleX: initialScale?.x,
              initialScaleY: initialScale?.y,
            });
          }
        };

        if (i === 0) {
          spawnFn();
        } else {
          this.scene.time.delayedCall(burstDelay * i, spawnFn);
        }
      }
    }

    // Emit ammo changed event
    const ammo = slot.getAmmo();
    this.scene.events.emit('ammoChanged', {
      slot: slotType,
      weaponId: weaponDef?.id,
      ammo,
    });

    // Emit weaponEmpty when ammo depletes to 0 (with cooldown to prevent spam)
    if (ammo === 0 && weaponDef) {
      const lastNotification = this.lastEmptyNotification.get(slotType) ?? 0;
      if (time - lastNotification >= RuntimeManagerWeapon.EMPTY_NOTIFICATION_COOLDOWN) {
        this.lastEmptyNotification.set(slotType, time);
        this.scene.events.emit('weaponEmpty', {
          slot: slotType,
          weapon: weaponDef,
        });
      }
    }

    return true;
  }

  /**
   * Get a weapon slot
   */
  getSlot(slotType: SlotType): WeaponSlot | undefined {
    return this.slots.get(slotType);
  }

  /**
   * Unlock a weapon
   */
  unlockWeapon(weaponId: string, ammo?: number): boolean {
    const weaponDef = DataManagerWeapon.get(weaponId);
    if (!weaponDef) return false;

    const slot = this.slots.get(weaponDef.slot);
    if (!slot) return false;

    return slot.addWeapon(weaponId, ammo);
  }

  /**
   * Add ammo to a weapon
   */
  addAmmo(weaponId: string, amount: number): boolean {
    const weaponDef = DataManagerWeapon.get(weaponId);
    if (!weaponDef) return false;

    const slot = this.slots.get(weaponDef.slot);
    if (!slot) return false;

    const success = slot.addAmmo(weaponId, amount);

    if (success) {
      this.scene.events.emit('ammoChanged', {
        slot: weaponDef.slot,
        weaponId,
        ammo: slot.getAmmo(weaponId),
      });
    }

    return success;
  }

  /**
   * Cycle weapon in a slot
   */
  cycleWeapon(slotType: SlotType): void {
    const slot = this.slots.get(slotType);
    if (!slot) return;

    const newWeaponId = slot.cycleWeapon();

    this.scene.events.emit('weaponSwitched', {
      slot: slotType,
      newWeaponId,
    });
  }

  /**
   * Enable/disable weapon system
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (!enabled) {
      // Stop tractor if active
      const tractorSlot = this.slots.get('tractor');
      if (tractorSlot?.getIsHolding()) {
        tractorSlot.stopHold();
        this.scene.events.emit('tractorBeamStopped');
      }
    }
  }

  /**
   * Check if enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Update biomatter (call when player collects biomatter)
   */
  addBiomatter(amount: number): void {
    this.biomatter += amount;
  }

  /**
   * Get current biomatter
   */
  getBiomatter(): number {
    return this.biomatter;
  }

  /**
   * Get swap cooldown remaining (ms)
   */
  getSwapCooldownRemaining(currentTime: number): number {
    const elapsed = currentTime - this.lastSwapTime;
    return Math.max(0, this.swapConfig.cooldownMs - elapsed);
  }

  /**
   * Check if swap is on cooldown
   */
  isSwapOnCooldown(currentTime: number): boolean {
    return this.getSwapCooldownRemaining(currentTime) > 0;
  }

  /**
   * Get swap cost
   */
  getSwapCost(): number {
    return this.swapConfig.cost;
  }

  /**
   * Get state for saving
   */
  getState(): RuntimeManagerWeaponState {
    const slots: Record<SlotType, ReturnType<WeaponSlot['getState']>> = {
      gun: { activeWeaponId: null, weapons: [] },
      tractor: { activeWeaponId: null, weapons: [] },
      bomb: { activeWeaponId: null, weapons: [] },
    };

    for (const [slotType, slot] of this.slots) {
      slots[slotType] = slot.getState();
    }

    return { slots };
  }

  /**
   * Load state
   */
  loadState(state: RuntimeManagerWeaponState): void {
    for (const [slotType, slotState] of Object.entries(state.slots)) {
      const slot = this.slots.get(slotType as SlotType);
      if (slot) {
        slot.loadState(slotState);
      }
    }
  }

  /**
   * Get ammo info for UI
   */
  getAmmoInfo(): Record<SlotType, { weaponId: string; ammo: number }> {
    const info: Record<SlotType, { weaponId: string; ammo: number }> = {
      gun: { weaponId: '', ammo: -1 },
      tractor: { weaponId: '', ammo: -1 },
      bomb: { weaponId: '', ammo: 0 },
    };

    for (const [slotType, slot] of this.slots) {
      const weapon = slot.getActiveWeaponDefinition();
      if (weapon) {
        info[slotType] = {
          weaponId: weapon.id,
          ammo: slot.getAmmo(),
        };
      }
    }

    return info;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.scene.events.off('collectPowerup', this.onCollectPowerup, this);
    this.scene.events.off('playerDied', this.onPlayerDied, this);
  }
}
